import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import { DiscrepancyService } from "@services/ingest/discrepancyService";
import type { Discrepancy, MissingMembers } from "@models/Discrepancy.model";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { Paths } from "@/paths";
import { formatDay } from "./DataIssuesPage";
import AddCorrectionDialog from "./AddCorrectionDialog";
import CorrectionsTable from "./CorrectionsTable";

const service = new DiscrepancyService();

/*
    One discrepancy (MPN-2009): what the file did compared with the one before it, who is
    missing, and the two ways to close it — accept as is with a reason, or add a correction.
    A held file (MPN-2121) has not loaded: either decision loads it, with the correction
    applied if there is one.
*/
export default function DiscrepancyPage() {
  useDocumentTitle("Data issue");
  const { discrepancyId } = useParams();
  const id = Number(discrepancyId);
  const [d, setD] = useState<Discrepancy | null>(null);
  const [missing, setMissing] = useState<MissingMembers | null>(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(() => {
    service.get(id).then(setD).catch((err) => setError(err instanceof Error ? err.message : "Failed to load"));
    service.missingMembers(id).then(setMissing).catch(() => setMissing(null));
  }, [id]);

  useEffect(load, [load]);

  const accept = async () => {
    setSaving(true);
    setError(null);
    try {
      setD(await service.accept(id, note));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not accept");
    } finally {
      setSaving(false);
    }
  };

  if (!d) return <Box sx={{ p: 3 }}>{error ? <Alert severity="error">{error}</Alert> : <LinearProgress />}</Box>;

  const isMissing = d.kind === "missing_from_file";
  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2, maxWidth: 1100 }}>
      <Link to={`${import.meta.env.BASE_URL}${Paths.DATA_ISSUES}`} style={{ fontSize: 13, color: "#64748b" }}>
        ‹ Data issues
      </Link>
      <Box>
        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          <Chip size="small" label={isMissing ? "Members missing from a file" : "Reported"} />
          <Chip size="small" label={d.status} color={d.status === "open" ? "warning" : "default"} />
        </Box>
        <Typography variant="h6" fontWeight={600}>{d.reason}</Typography>
        <Typography sx={{ color: "#64748b", fontSize: 13 }}>
          {d.organization_name} · {d.population_name ?? "—"} · {d.source === "system" ? "found by the platform" : `reported by ${d.created_by ?? "Ops"}`} {formatDay(d.create_date)}
          {d.file?.file_name ? ` · ${d.file.file_name}` : ""}
          {d.client_request ? ` · ${d.client_request}` : ""}
        </Typography>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {d.holds_file && (
        <Alert severity="warning">
          <strong>{d.file?.file_name ?? "This file"} is held and has not loaded.</strong> Accepting this, or
          adding a correction, loads it — with the correction applied. Later files of this spec wait behind it.
        </Alert>
      )}

      {isMissing && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography fontWeight={600} sx={{ mb: 1 }}>
            Compared with {d.compared_file?.file_name ?? "the previous file"} · {formatDay(d.compared_file?.as_of ?? null)}
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1.5, mb: 2 }}>
            <Count label="In both files" value={d.in_both_count} />
            <Count label="Missing now" value={d.missing_count} highlight />
            <Count label="New in this file" value={d.new_count} />
          </Box>
          {missing && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Missing member</TableCell>
                  <TableCell>Last seen</TableCell>
                  <TableCell>In this population</TableCell>
                  <TableCell>Converted</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {missing.members.map((m, i) => (
                  <TableRow key={m.member_id ?? i}>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{m.member_id ?? "—"}</TableCell>
                    <TableCell>{formatDay(m.last_seen)}</TableCell>
                    <TableCell>{m.held_out ? "Held out" : "Served"}</TableCell>
                    <TableCell>{m.converted ? `Yes · ${formatDay(m.converted_on)}` : "No"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {missing && missing.total > missing.members.length && (
            <Typography sx={{ color: "#64748b", fontSize: 12, mt: 1 }}>
              Showing {missing.members.length} of {missing.total} still missing.
            </Typography>
          )}
        </Paper>
      )}

      {d.status === "open" ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { md: "1fr 1fr" }, gap: 2 }}>
          <Paper variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography fontWeight={600}>Accept as is</Typography>
            <Typography sx={{ color: "#64748b", fontSize: 13 }}>
              {d.holds_file
                ? "The file loads as it is. The people it dropped keep their history. Use this when the change is expected."
                : "Nothing changes. The people stay in the population with their history. Use this when the change is expected."}
            </Typography>
            <TextField size="small" label="Reason" required multiline minRows={2} value={note}
              placeholder="e.g. Students who graduated in September. Client confirmed by email."
              onChange={(e) => setNote(e.target.value)} />
            <Box>
              <Button variant="outlined" onClick={accept} disabled={saving || !note.trim()} sx={{ textTransform: "none" }}>
                Accept
              </Button>
            </Box>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography fontWeight={600}>Correct it</Typography>
            <Typography sx={{ color: "#64748b", fontSize: 13 }}>
              Pick the people, then choose what happens to them: stop serving them, or remove
              them from this population.
            </Typography>
            <Box>
              <Button variant="contained" onClick={() => setAdding(true)} sx={{ textTransform: "none" }}>
                Add a correction
              </Button>
            </Box>
          </Paper>
        </Box>
      ) : d.status === "accepted" && (
        <Alert severity="info">
          Accepted by {d.resolved_by ?? "Ops"} {formatDay(d.resolved_at)}: "{d.resolution_note}"
        </Alert>
      )}

      {d.status !== "accepted" && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography fontWeight={600}>Corrections for this discrepancy</Typography>
            {d.status === "resolved" && (
              <Button size="small" sx={{ ml: "auto", textTransform: "none" }} onClick={() => setAdding(true)}>
                Add another batch
              </Button>
            )}
          </Box>
          <CorrectionsTable discrepancyId={d.id} reloadKey={reloadKey} />
        </Paper>
      )}

      {adding && (
        <AddCorrectionDialog
          discrepancy={d}
          open
          onClose={() => setAdding(false)}
          onApplied={() => {
            setAdding(false);
            setReloadKey((k) => k + 1);
            load();
          }}
        />
      )}
    </Box>
  );
}

function Count({ label, value, highlight = false }: { label: string; value: number | null; highlight?: boolean }) {
  return (
    <Box sx={{ border: highlight ? "2px solid #6b43c9" : "1px solid #e5e7eb", borderRadius: 2, p: 1.5 }}>
      <Typography sx={{ fontSize: 12, color: highlight ? "#5b21b6" : "#64748b" }}>{label}</Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 600 }}>{value?.toLocaleString() ?? "—"}</Typography>
    </Box>
  );
}
